import React from 'react';
import i18n from 'i18n-js';
import PropTypes from 'prop-types';
import customStyles from './Table.styled';
import DataTable from 'react-data-table-component';
import TablePaginationActions from './CustomTablePagination';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import { LinearProgress } from '@mui/material';

const sortIcon = <ArrowDownward />;

const LinearIndeterminate = () => {
  return (
    <div style={{ width: '100%', marginBottom: 40 }}>
      <LinearProgress />
    </div>
  );
};

const NoDataComponent = () => {
  return <div style={{ padding: '24px' }}>{i18n.t('common.no_records_to_display')}</div>;
};

const MaterialTable = ({
  columns,
  data,
  page,
  limit,
  onSort,
  loading,
  totalRows,
  pagination,
  fixedHeader,
  extraStyles,
  currentPage,
  rowsPerPage,
  onPageChange,
  selectableRows,
  handlePageChange,
  handleRowClicked,
  progressComponent,
  clearSelectedRows,
  onRowsPerPageChange,
  handlePerRowsChange,
  handleSelectedRowsChange,
  ...tableProps
}) => {
  const handleSort = (column, sortDirection) => {
    onSort({ column, sortDirection });
  };

  const paginationComponent = (props) => (
    <TablePaginationActions
      {...tableProps}
      {...props}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handlePerRowsChange}
      currentPage={Number(page) || Number(currentPage)}
      rowsPerPage={Number(limit) || Number(rowsPerPage)}
    />
  );

  const useCustomPagination = page && limit;

  return (
    <div data-cy={tableProps['data-cy']}>
      <DataTable
        data={data}
        noHeader={true}
        persistTableHead
        highlightOnHover
        paginationServer
        columns={columns}
        onSort={handleSort}
        sortIcon={sortIcon}
        pagination={pagination}
        fixedHeader={fixedHeader}
        progressPending={loading}
        selectableRows={selectableRows}
        paginationTotalRows={totalRows}
        // Nasty bug, as page change was triggered twice at the default pagination and
        // the custom one if the custom pagination is provided it should not provide the
        // handler to the default paginator
        onChangePage={useCustomPagination ? undefined : handlePageChange}
        onRowClicked={handleRowClicked}
        clearSelectedRows={clearSelectedRows}
        noDataComponent={<NoDataComponent />}
        progressComponent={progressComponent}
        onChangeRowsPerPage={handlePerRowsChange}
        onSelectedRowsChange={handleSelectedRowsChange}
        customStyles={{ ...customStyles, ...extraStyles }}
        sortServer={true}
        {...(useCustomPagination
          ? { paginationComponent }
          : { onChangePage: onPageChange, onChangeRowsPerPage: onRowsPerPageChange })}
        {...tableProps}
      />
    </div>
  );
};

MaterialTable.propTypes = {
  data: PropTypes.array,
  page: PropTypes.number,
  onSort: PropTypes.func,
  limit: PropTypes.number,
  loading: PropTypes.bool,
  columns: PropTypes.array,
  pagination: PropTypes.bool,
  extraStyles: PropTypes.any,
  fixedHeader: PropTypes.bool,
  totalRows: PropTypes.number,
  onPageChange: PropTypes.func,
  currentPage: PropTypes.number,
  rowsPerPage: PropTypes.number,
  selectableRows: PropTypes.bool,
  disabledField: PropTypes.string,
  handleRowClicked: PropTypes.func,
  handlePageChange: PropTypes.func,
  clearSelectedRows: PropTypes.bool,
  disableSelectable: PropTypes.bool,
  selectableField: PropTypes.string,
  progressComponent: PropTypes.node,
  handlePerRowsChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  handleSelectedRowsChange: PropTypes.func,
};

MaterialTable.defaultProps = {
  data: [],
  columns: [],
  totalRows: 0,
  loading: false,
  extraStyles: {},
  pagination: true,
  onSort: () => {},
  fixedHeader: false,
  selectableRows: false,
  disableSelectable: false,
  clearSelectedRows: false,
  disabledField: 'disabled',
  handleRowClicked: () => {},
  handlePageChange: () => {},
  selectableField: 'selected',
  handlePerRowsChange: undefined,
  handleSelectedRowsChange: () => {},
  progressComponent: <LinearIndeterminate />,
};

export default MaterialTable;
